import * as React from "react";
import { useState, useContext, useEffect, FunctionComponent } from "react";

// MUI
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import { createTheme, styled, Theme, ThemeProvider, useTheme } from '@mui/material/styles';

// Custom Components
import NavDrawer from "@/ui/NavDrawer/NavDrawer";
import menuRoutes from "@/routes/menu";
import UserContext from "@/components/UserContext/UserContext";
import useNavigateByPermissions from "@/components/useNavigateByPermissions"
import NavDrawerContext, { NavDrawerContextType } from "@/components/NavDrawerContext/NavDrawerContext";
import NavDrawerProvider from "@/components/NavDrawerContext/NavDrawerProvider";
import Loader from "@/ui/Loader/Loader";
import AppHeader from "@/ui/Header/AppHeader";

// Types
import { IAppPage } from "@/@types/ui/AppPage";
import { UserContextType } from "@/@types/context/UserContext";
import OperationsHeader from "../Header/OperationsHeader";
import { blue, grey } from "@mui/material/colors";
import DialogProvider from "../DialogContext/DialogProvider";
import DialogContainer from "../DialogContext/DialogContainer";

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(1),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: 0,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0
  }),
}));

const AppPage: FunctionComponent<IAppPage> = (props) => {
  const userContext = useContext(UserContext) as UserContextType;
  const navDrawerContext = useContext(NavDrawerContext) as NavDrawerContextType;

  const { needPermission = [], menu, modul } = props;
  const initialSidebarState = { open: JSON.parse(localStorage.getItem('sidebar') as string) ? JSON.parse(localStorage.getItem('sidebar') as string).open : false };
  localStorage.setItem('sidebar', JSON.stringify(initialSidebarState))
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(initialSidebarState.open);
  const toggleSidebar = (): void => {
    setSidebarOpen((prevState) => !prevState);

    var sidebarSettings = JSON.parse(localStorage.getItem('sidebar') as string);
    sidebarSettings.open = !sidebarSettings.open;

    localStorage.setItem('sidebar', JSON.stringify(sidebarSettings));
  };

  const [collapsedNavLinks, setCollapsedNavLinks] = useState<string[]>(
    navDrawerContext.sidebarCollapsed
  );

  const navigateFn = useNavigateByPermissions();

  const forbidden: boolean = needPermission.length > 0 && !userContext.hasAnyPermission(needPermission);

  useEffect(() => {
    // console.log("HERE", userContext, userContext.loaded, forbidden)
    if (userContext && userContext.loaded && forbidden) {
      console.warn("Tried to access forbidden page, Redirecting...");
      navigateFn(userContext.permissionIds);
    }
  }, [userContext.loaded, forbidden]);

  const handleNavbarLinkCollapse = (name: string): void => {
    var collapsedCopy = [...collapsedNavLinks];

    // add name to collapseedcopy or remove from it if it exists
    let found = false;
    for (let i = 0; i < collapsedCopy.length; i++) {
      if (collapsedCopy[i] === name) {
        found = true;
        collapsedCopy.splice(i, 1);
      }
    }

    if (!found) {
      collapsedCopy.push(name);
    }

    var sidebarSettings = JSON.parse(localStorage.getItem('sidebar') as string);
    sidebarSettings.collapsed = collapsedCopy;

    localStorage.setItem('sidebar', JSON.stringify(sidebarSettings));

    setCollapsedNavLinks(collapsedCopy);
  };

  return userContext && userContext.loaded && !forbidden ? (
    <ThemeProvider theme={outerTheme => ({
      ...outerTheme,
      palette: {
        ...outerTheme.palette,
        primary: theme.palette.primary,
        secondary: theme.palette.secondary
      },
      spacing: theme.spacing,
      components: {
        ...outerTheme.components,
        MuiTableRow: {
          styleOverrides: {
            root: {
              '&.Mui-selected': {
                backgroundColor: 'rgba(21, 101, 192, 0.24)'
              }
            }
          }
        }
      }
    })}>
      <DialogProvider>
        <NavDrawerProvider>
          <div id="page">
            <OperationsHeader modul={modul} toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />
            <Box m={0} sx={{ display: { xs: "none", md: "block" } }}>
              <NavDrawer
                menu={menu || menuRoutes}
                open={sidebarOpen}
                toggleOpen={toggleSidebar}
                collapsedNavLinks={collapsedNavLinks}
                onNavLinkCollapse={handleNavbarLinkCollapse}
              />
            </Box>
            <NavDrawerContext.Consumer>
              {({ sidebarOpen }) =>
                <Main open={sidebarOpen}>
                  <Toolbar variant="dense" />
                  <div id="content">{props.children}</div>
                </Main>
              }
            </NavDrawerContext.Consumer>
          </div>
        </NavDrawerProvider>
        <DialogContainer />
      </DialogProvider>
    </ThemeProvider>
  ) : (
    <Loader open={true} />
  );
};

export default AppPage;


const theme: Theme = createTheme({
  palette: {
    primary: {
      main: blue[800],
      dark: blue[900],
      light: blue[700]
    },
    secondary: {
      main: grey[800],
      dark: grey[900],
      light: grey[400]
    }
  },
  // spacing: 1,
});
